// ============================================
// Stores Hooks - TanStack Query
// ============================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type {
  Store,
  StoreStatus,
  StoreListParams,
  StoreStats,
  CreateStoreRequest,
  UpdateStoreRequest,
  PaginationParams,
} from '@conai/shared-types';
import { useStoresService } from '../providers/ApiProvider';
import { useStoreSelectionStore } from '../stores';
import { queryKeys } from '../config/query-client';
import { useI18n } from '../i18n';

interface StoreFilters extends PaginationParams {
  /** Model Store chỉ có `business_status`, không có `status` (BE 26/08/2026). */
  business_status?: StoreStatus;
  search?: string;
  [key: string]: unknown;
}

function mapStoreFilters(filters: StoreFilters): StoreListParams {
  return {
    page: filters.page,
    page_size: filters.page_size,
    search: filters.search,
    business_status: filters.business_status,
  };
}

/**
 * Hook to list stores with pagination and filters
 */
export function useStores(filters: StoreFilters = {}) {
  const storesService = useStoresService();
  const mappedFilters = mapStoreFilters(filters);

  return useQuery({
    queryKey: queryKeys.stores.list(filters),
    queryFn: () => storesService.list(mappedFilters),
  });
}

/**
 * Hook to get store stats
 */
export function useStoresStats() {
  const storesService = useStoresService();

  return useQuery<StoreStats>({
    queryKey: [...queryKeys.stores.all, 'stats'] as const,
    queryFn: () => storesService.getStats(),
  });
}

/**
 * Hook to get a single store by ID
 */
export function useStore(id: string, options?: { enabled?: boolean }) {
  const storesService = useStoresService();

  return useQuery({
    queryKey: queryKeys.stores.detail(id),
    queryFn: () => storesService.getById(id),
    enabled: options?.enabled !== false && !!id,
  });
}

/**
 * Hook to create a new store
 */
export function useCreateStore() {
  const storesService = useStoresService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (data: CreateStoreRequest) => storesService.create(data),
    onSuccess: (newStore) => {
      // Invalidate stores list
      queryClient.invalidateQueries({ queryKey: queryKeys.stores.lists() });
      // Invalidate stores stats to update card values (Store, Warehouse) in real-time
      queryClient.invalidateQueries({ queryKey: [...queryKeys.stores.all, 'stats'] });
      toast.success(t('toastStoreCreated', { name: newStore.name }));
    },
  });
}

/**
 * Hook to update a store
 */
export function useUpdateStore() {
  const storesService = useStoresService();
  const queryClient = useQueryClient();
  const { selectedStore, setSelectedStore } = useStoreSelectionStore();
  const { t } = useI18n();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateStoreRequest }) =>
      storesService.patch(id, data),
    onSuccess: (updatedStore) => {
      // Invalidate detail to force re-fetch with full fields
      queryClient.invalidateQueries({ queryKey: queryKeys.stores.detail(updatedStore.id) });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: queryKeys.stores.lists() });
      // Invalidate stores stats to update card values (Store, Warehouse) in real-time
      queryClient.invalidateQueries({ queryKey: [...queryKeys.stores.all, 'stats'] });
      // Update selected store if it's the one being updated
      if (selectedStore?.id === updatedStore.id) {
        setSelectedStore(updatedStore);
      }
      toast.success(t('toastStoreUpdated', { name: updatedStore.name }));
    },
  });
}

/**
 * Hook to delete a store
 */
export function useDeleteStore() {
  const storesService = useStoresService();
  const queryClient = useQueryClient();
  const { selectedStore, clearSelection } = useStoreSelectionStore();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (id: string) => storesService.delete(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: queryKeys.stores.detail(deletedId) });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: queryKeys.stores.lists() });
      // Invalidate stores stats to update card values (Store, Warehouse) in real-time
      queryClient.invalidateQueries({ queryKey: [...queryKeys.stores.all, 'stats'] });
      // Clear selection if deleted store was selected
      if (selectedStore?.id === deletedId) {
        clearSelection();
      }
      toast.success(t('toastStoreDeleted'));
    },
  });
}

/**
 * Hook to load and cache available stores for selection
 */
export function useAvailableStores() {
  const storesService = useStoresService();
  const { setAvailableStores, setLoadingStores } = useStoreSelectionStore();

  return useQuery({
    queryKey: queryKeys.stores.list({ business_status: 'ACTIVE', page_size: 100 }),
    queryFn: async () => {
      setLoadingStores(true);
      const result = await storesService.list({ business_status: 'ACTIVE', page_size: 100 });
      setAvailableStores(result.results ?? []);
      setLoadingStores(false);
      return result;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}
